import type { ReactNode } from 'react';

interface Props {
  label: string;
  required?: boolean;
  /**
   * Nhãn đã nổi lên viền chưa. Nơi gọi tự quyết — thường là `focused || đã có giá trị`.
   * Cố ý KHÔNG tự suy: component này không biết giá trị nằm ở Form hay ở state.
   */
  isFloating: boolean;
  /**
   * Tô đỏ nhãn. `FloatingField` KHÔNG dùng prop này — nó để CSS
   * `.floating-field:has(.ant-form-item-has-error)` bắt trạng thái lỗi của antd.
   * Nơi gọi controlled (không có `Form.Item`) thì `:has()` không có gì để bắt,
   * nên phải nói ra bằng prop.
   */
  hasError?: boolean;
  className?: string;
  suffix?: ReactNode;
  children: ReactNode;
}

/**
 * Phần NHÌN của ô nhãn nổi: khung `.floating-field`, nhãn tuyệt đối, slot suffix.
 *
 * Tách khỏi `FloatingField` vì có hai loại nơi gọi:
 *  - **trong antd Form** — `FloatingField` tự dựng `Form.Item name=...`;
 *  - **ngoài Form, controlled** — `PackageInfoFields`. Bốn field `package_*` ĐÃ
 *    được `FulfillmentFormSection` đăng ký ẩn qua `HIDDEN_REGISTERED_FIELDS`.
 *    Dựng thêm `Form.Item` trùng tên là tái tạo bug TCR-21 (hai ô required, hai
 *    dòng lỗi); còn gỡ đăng ký ẩn thì bốn field biến mất khỏi `useWatch` ở mọi
 *    chế độ KHÔNG render khối gói hàng.
 *
 * Component này KHÔNG biết antd Form. ĐỪNG thêm `name` / `rules` vào đây —
 * làm vậy là kéo ngược lại đúng ràng buộc mà việc tách này gỡ ra.
 *
 * Tiền lệ cùng hướng: `CustomerSearchField` cũng dùng `.floating-field` mà không
 * có `Form.Item`, qua biến thể `.floating-field--search`.
 */
export function FloatingLabelBox({
  label,
  required,
  isFloating,
  hasError,
  className,
  suffix,
  children,
}: Props): JSX.Element {
  const wrapperClass = [
    'floating-field',
    isFloating ? 'is-floating' : '',
    hasError ? 'has-error' : '',
    suffix ? 'has-suffix' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClass}>
      {children}
      <span className="floating-field-label">
        {label}
        {required && <span className="floating-field-required">*</span>}
      </span>
      {suffix && <span className="floating-field-suffix">{suffix}</span>}
    </div>
  );
}
